// /communities/:slug/boundary — polygon editor for a community's
// perimeter. Draw one polygon that defines the community's footprint;
// used by the admin map view + (later) by the local-relevance ranker.
//
// Backing endpoint: PATCH /api/admin/map/communities/{id} with
// { set_boundary: true, boundary: <GeoJSON polygon or null> }.
// Staff-only for now.
//
// Approach: minimum viable. Each click drops a MapLibre Marker (a plain
// DOM element on top of the map — always renders, no source/filter
// gymnastics). "Save" packages the marker coords into a Polygon and
// sends. The existing boundary is rendered as a filled polygon via the
// same simple source pattern AdminMap already uses in production.
// A live "polygon-in-progress" preview is deferred to v2 — the
// individual vertex dots are enough feedback while drawing.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as maplibregl from 'maplibre-gl';
import { type Map as MLMap, Marker, NavigationControl } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useApi, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

const OSM_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const CAPE_TOWN: [number, number] = [18.4152, -33.9376];


interface CommunityMarker {
    id:       string;
    slug:     string;
    name:     string;
    lat?:     number | null;
    lon?:     number | null;
    boundary?: GeoJSON.Polygon | null;
}

export function CommunityBoundaryEditor() {
    const { slug } = useParams<{ slug: string }>();
    const api = useApi();
    const navigate = useNavigate();
    const { authed, login } = useAuth();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const mapRef       = useRef<MLMap | null>(null);
    // The vertex markers — array of MapLibre Marker instances. Each has
    // its own DOM element + lngLat. We read coords off them at save time.
    const markersRef   = useRef<Marker[]>([]);
    const drawingRef   = useRef<boolean>(false);

    const [community, setCommunity]   = useState<CommunityMarker | null>(null);
    const [drawing,   setDrawing]     = useState(false);
    const [count,     setCount]       = useState(0);
    const [closed,    setClosed]      = useState(false);
    const closedRef = useRef(false);
    // Vertices in React state so the SVG overlay re-renders on add/remove.
    // Parallel to markersRef (which holds the MapLibre Marker instances)
    // — kept in sync in the click handler.
    const [vertices, setVertices] = useState<Array<[number, number]>>([]);
    // Bumped on every map move/zoom so the SVG overlay re-projects.
    const [mapTick, setMapTick] = useState(0);
    const [status,    setStatus]      = useState<string>('');
    const [saving,    setSaving]      = useState(false);
    const [err,       setErr]         = useState<string>('');

    const load = useCallback(async () => {
        if (!authed || !slug) return;
        try {
            const r = await api.get<{ communities: CommunityMarker[] }>('/api/admin/map/communities');
            const c = (r.communities ?? []).find(x => x.slug.toLowerCase() === slug.toLowerCase());
            if (!c) { setErr(`No community with slug "${slug}"`); return; }
            setCommunity(c);
        } catch (e) {
            if (e instanceof ApiError && e.status === 403) {
                setErr('Staff-only page. You need to be on the admin allowlist to edit boundaries.');
                return;
            }
            setErr((e as Error).message);
        }
    }, [api, authed, slug]);

    useEffect(() => { load(); }, [load]);

    // No addVertex function here — the logic is inlined into the map
    // init effect below. Reason: any addVertex declared here is captured
    // as a stale closure by the click handler registered in the effect.
    // Every HMR re-render would create a new addVertex the click handler
    // never sees. Inlining + reading everything through refs avoids that.

    // Boot the map + install click handler.
    useEffect(() => {
        if (!community || !containerRef.current || mapRef.current) return;

        const center: [number, number] = community.lat != null && community.lon != null
            ? [community.lon, community.lat]
            : polygonCentroid(community.boundary) ?? CAPE_TOWN;

        const m = new maplibregl.Map({
            container: containerRef.current,
            style: minimalStyle(OSM_TILE_URL),
            center,
            zoom: 14,
        });
        m.addControl(new NavigationControl(), 'top-right');
        m.doubleClickZoom.disable();

        // Redraws the connecting line between current vertices. Called
        // after every add / remove. Kept inline (not a component method)
        // so no stale closures via HMR — reads markersRef directly.
        m.on('load', () => {
            // Existing saved boundary rendering moved to the SVG overlay
            // for reliability (see BoundarySvgOverlay). Just set the
            // initial status here.
            setStatus(community.boundary
                ? 'Existing boundary shown in green. Click "Start drawing" to replace it.'
                : 'Click "Start drawing", then click points around the community.');
        });

        // Canvas-level click — inlined so it can never get stuck on a
        // stale addVertex closure from a previous HMR render.
        const canvas = m.getCanvas();
        canvas.addEventListener('click', (evt) => {
            console.log('[boundary] canvas click fired, drawing=', drawingRef.current, 'closed=', closedRef.current);
            if (!drawingRef.current) return;
            if (closedRef.current) return; // Already closed — ignore further map clicks.
            const rect = canvas.getBoundingClientRect();
            const lngLat = m.unproject([
                (evt as MouseEvent).clientX - rect.left,
                (evt as MouseEvent).clientY - rect.top,
            ]);

            const el = document.createElement('div');
            Object.assign(el.style, {
                width: '18px', height: '18px', borderRadius: '50%',
                background: '#8b1919', border: '3px solid #fff',
                boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                cursor: 'pointer', zIndex: '999',
            });
            el.className = 'boundary-vertex';
            const marker = new Marker({ element: el }).setLngLat(lngLat).addTo(m);

            // Clicking a vertex:
            //   - If it's the FIRST vertex and we have ≥3 points → close.
            //   - Otherwise → remove it.
            el.addEventListener('click', (e2) => {
                e2.stopPropagation();
                e2.preventDefault();
                const isFirst = markersRef.current[0] === marker;
                if (isFirst && markersRef.current.length >= 3 && !closedRef.current) {
                    closedRef.current = true;
                    setClosed(true);
                    el.style.background = '#2b5e3f';   // green = closed anchor
                    el.style.width = '22px';
                    el.style.height = '22px';
                    setStatus(`Closed loop with ${markersRef.current.length} points. Hit "Save boundary" to persist.`);
                    setMapTick(t => t + 1);   // re-render SVG to draw the closing segment
                    return;
                }
                if (closedRef.current) return; // No edits after close — click Reopen first.
                marker.remove();
                markersRef.current = markersRef.current.filter(x => x !== marker);
                setCount(markersRef.current.length);
                setVertices(markersRef.current.map(mm => {
                    const p = mm.getLngLat();
                    return [p.lng, p.lat] as [number, number];
                }));
            });
            markersRef.current.push(marker);
            setCount(markersRef.current.length);
            setVertices(markersRef.current.map(mm => {
                const p = mm.getLngLat();
                return [p.lng, p.lat] as [number, number];
            }));
            if (markersRef.current.length >= 3) {
                setStatus(`${markersRef.current.length} points — click the first (red) dot to close the loop.`);
            }
        });

        // Fire re-renders on map interaction so the SVG overlay re-projects.
        m.on('move', () => setMapTick(t => t + 1));
        m.on('zoom', () => setMapTick(t => t + 1));
        m.on('resize', () => setMapTick(t => t + 1));

        mapRef.current = m;
        return () => { m.remove(); mapRef.current = null; };
    }, [community]);

    // Cursor: MapLibre owns the canvas cursor, override via getCanvas.
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        map.getCanvas().style.cursor = drawing ? 'crosshair' : '';
    }, [drawing, community]);

    function startDrawing() {
        clearMarkers();
        drawingRef.current = true;
        setDrawing(true);
        closedRef.current = false;
        setClosed(false);
        setErr('');
        setStatus('Drawing… click each corner. Click the first (red) dot to close the loop.');
    }

    function clearMarkers() {
        for (const m of markersRef.current) m.remove();
        markersRef.current = [];
        closedRef.current = false;
        setClosed(false);
        setCount(0);
        setVertices([]);
    }

    function reopenLoop() {
        closedRef.current = false;
        setClosed(false);
        const first = markersRef.current[0];
        if (first) {
            const el = first.getElement();
            el.style.background = '#8b1919';
            el.style.width = '18px';
            el.style.height = '18px';
        }
        setMapTick(t => t + 1);
        setStatus('Loop reopened. Add or remove points, then click the first dot again to close.');
    }

    async function save() {
        if (!community) return;
        if (!closedRef.current) { setErr('Close the loop first — click the first (red) dot.'); return; }
        const pts = markersRef.current.map(m => {
            const p = m.getLngLat();
            return [p.lng, p.lat] as [number, number];
        });
        if (pts.length < 3) { setErr('Need at least 3 points before saving.'); return; }
        const ring = [...pts, pts[0]];
        const polygon: GeoJSON.Polygon = { type: 'Polygon', coordinates: [ring] };
        setSaving(true); setErr('');
        try {
            await api.patch(`/api/admin/map/communities/${community.id}`, {
                set_boundary: true,
                boundary: polygon,
            });
            // Clear the drawing state + refetch community so the overlay
            // flips from the red drawing shape to the green persisted one.
            clearMarkers();
            drawingRef.current = false;
            setDrawing(false);
            await load();
            setStatus('✓ Saved.');
        } catch (e) {
            setErr('Save failed: ' + (e as Error).message);
        } finally {
            setSaving(false);
        }
    }

    async function deleteBoundary() {
        if (!community) return;
        if (!window.confirm('Delete this community\'s boundary? The map will show a centroid pin only.')) return;
        setSaving(true); setErr('');
        try {
            await api.patch(`/api/admin/map/communities/${community.id}`, {
                set_boundary: true,
                boundary: null,
            });
            clearMarkers();
            drawingRef.current = false;
            setDrawing(false);
            // Refetch community so the local community.boundary goes null
            // and the SVG overlay stops rendering the green existing shape.
            await load();
            setStatus('✓ Boundary removed.');
        } catch (e) {
            setErr('Delete failed: ' + (e as Error).message);
        } finally {
            setSaving(false);
        }
    }

    if (!authed) return (
        <div className="prelogin">
            <p>Log in.</p>
            <button type="button" className="plain" onClick={login}>Log in</button>
        </div>
    );

    return (
        <main style={{ padding: '20px 24px 60px', maxWidth: 1200, margin: '0 auto' }}>
            <Link to={`/communities/${slug}/dashboard`}
                  style={{ display: 'inline-block', marginBottom: 12, color: '#666', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1.5 }}>
                ← Back to dashboard
            </Link>
            <h1 style={{ margin: 0, fontFamily: 'Newsreader, serif', fontSize: 28 }}>
                Boundary — {community?.name ?? slug}
            </h1>
            <p style={{ color: '#666', fontSize: 13, margin: '4px 0 12px', fontStyle: 'italic' }}>
                Hit "Start drawing", then click each corner around the perimeter.
                Click any non-first vertex to remove it. Click the FIRST (red) dot
                when you have at least 3 points to close the loop — it turns green
                and Save unlocks. A saved boundary shows filled green after reload.
            </p>
            {err && <p style={{ color: '#8b1919' }}>{err}</p>}

            <div style={{ position: 'relative', width: '100%', height: 'calc(100vh - 320px)', minHeight: 460 }}>
                <div ref={containerRef}
                     className="newsprint-map"
                     style={{ position: 'absolute', inset: 0, border: '1px solid var(--ink)' }} />
                <BoundarySvgOverlay
                    map={mapRef.current}
                    vertices={vertices}
                    closed={closed}
                    existing={vertices.length === 0 && community?.boundary ? community.boundary : null}
                    tick={mapTick}
                />
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <button type="button" onClick={startDrawing} disabled={saving}
                        style={btn(drawing && !closed ? '#2b5e3f' : 'var(--ink)', false)}>
                    {drawing && !closed ? `Drawing… (${count})` : closed ? `Loop closed (${count})` : 'Start drawing'}
                </button>
                {closed && (
                    <button type="button" onClick={reopenLoop} disabled={saving}
                            style={btnGhost(saving)}>
                        Reopen loop
                    </button>
                )}
                <button type="button" onClick={clearMarkers} disabled={saving || count === 0}
                        style={btnGhost(saving || count === 0)}>
                    Clear
                </button>
                <button type="button" onClick={save} disabled={saving || !closed}
                        style={btn('#2b5e3f', saving || !closed)}>
                    {saving ? 'Saving…' : closed ? `Save boundary (${count} points)` : `Close loop to save`}
                </button>
                <button type="button" onClick={deleteBoundary} disabled={saving || !community?.boundary}
                        style={btnDanger(saving || !community?.boundary)}>
                    Delete saved boundary
                </button>
                <button type="button" onClick={() => navigate(`/communities/${slug}/dashboard`)}
                        style={btnGhost(false)}>
                    Cancel
                </button>
                <span style={{ marginLeft: 'auto', color: '#666', fontSize: 12 }}>{status}</span>
            </div>
        </main>
    );
}

// Absolute-positioned SVG on top of the map. Reads vertex lng/lats from
// props + the map's current camera to project each into pixel space, then
// draws a polyline (or polygon when closed). React-controlled DOM so no
// dependency on MapLibre source/layer rendering, which has been unreliable
// in this setup (see the two AI diagnoses in the chat log).
function BoundarySvgOverlay({ map, vertices, closed, existing, tick }: {
    map: MLMap | null;
    vertices: Array<[number, number]>;
    closed: boolean;
    existing: GeoJSON.Polygon | null;
    tick: number;
}) {
    void tick;   // re-render trigger only
    if (!map) return null;

    // Existing saved boundary — filled green, drawn beneath any editing
    // in-progress shape. Hidden as soon as the user starts drawing (parent
    // clears `existing` in that case).
    const existingPoints = existing?.coordinates?.[0]
        ? existing.coordinates[0].map(([lng, lat]) => {
            const p = map.project([lng, lat]);
            return `${p.x},${p.y}`;
          }).join(' ')
        : '';

    const editingPoints = vertices.length >= 2
        ? (closed ? [...vertices, vertices[0]] : vertices).map(([lng, lat]) => {
            const p = map.project([lng, lat]);
            return `${p.x},${p.y}`;
          }).join(' ')
        : '';

    if (!existingPoints && !editingPoints) return null;

    return (
        <svg
            style={{
                position: 'absolute', inset: 0,
                pointerEvents: 'none',
                width: '100%', height: '100%',
            }}
        >
            {existingPoints && (
                <polyline
                    points={existingPoints}
                    fill="rgba(43, 94, 63, 0.18)"
                    stroke="#2b5e3f"
                    strokeWidth={2}
                    strokeLinejoin="round"
                />
            )}
            {editingPoints && (
                <polyline
                    points={editingPoints}
                    fill={closed ? 'rgba(139, 25, 25, 0.15)' : 'none'}
                    stroke="#8b1919"
                    strokeWidth={3}
                    strokeLinejoin="round"
                />
            )}
        </svg>
    );
}

function polygonCentroid(p?: GeoJSON.Polygon | null): [number, number] | null {
    if (!p || !p.coordinates?.[0]?.length) return null;
    const ring = p.coordinates[0];
    let sx = 0, sy = 0;
    for (const [x, y] of ring) { sx += x; sy += y; }
    return [sx / ring.length, sy / ring.length];
}

function minimalStyle(tilesURL: string): maplibregl.StyleSpecification {
    return {
        version: 8,
        sources: {
            osm: {
                type: 'raster',
                tiles: [tilesURL],
                tileSize: 256,
                attribution: '© OpenStreetMap contributors',
            },
        },
        layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
    };
}

const btn = (bg: string, disabled: boolean): React.CSSProperties => ({
    padding: '8px 20px', background: disabled ? '#ccc' : bg, color: '#fff', border: 0,
    cursor: disabled ? 'default' : 'pointer',
    textTransform: 'uppercase', letterSpacing: 1, fontSize: 12,
});
const btnDanger = (disabled: boolean): React.CSSProperties => ({
    padding: '8px 20px', background: 'transparent',
    color: disabled ? '#ccc' : '#8b1919',
    border: '1px solid ' + (disabled ? '#ccc' : '#8b1919'),
    cursor: disabled ? 'default' : 'pointer',
    textTransform: 'uppercase', letterSpacing: 1, fontSize: 12,
});
const btnGhost = (disabled: boolean): React.CSSProperties => ({
    padding: '8px 20px', background: 'transparent',
    color: disabled ? '#ccc' : 'var(--muted)',
    border: '1px solid ' + (disabled ? '#eee' : 'var(--line)'),
    cursor: disabled ? 'default' : 'pointer',
    textTransform: 'uppercase', letterSpacing: 1, fontSize: 12,
});
